import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { type PriorityRequestListItem } from "@/services/api/teacherPriority";
import { type SubstitutionRequestDocument } from "@/types/teacher-request";
import { type RequestLesson, teacherApi } from "@/services/api/teacher";
import {
  getDisplayStatus,
  getStatusColor,
  getStatusLabel,
  getBorderLColor,
  isFinalRequestStatus,
} from "@/utils/statusHelpers";
import { formatMatchEta } from "@/utils/matchEta";
import { DECISION_TYPE_LABELS } from "@/types/director";
import { getLessonRowStyle } from "@/utils/lessonRowColors";
import { CoverageBar } from "./CoverageBar";
import { PriorityCascadeIndicator } from "./PriorityCascadeIndicator";
import { CascadeLegendTrigger } from "./CascadeLegendTrigger";
import { PriorityCardTimelineTab } from "./PriorityCardTimelineTab";
import { ResponseCountdown } from "../ui/ResponseCountdown";
import { teacherPriorityApi } from "@/services/api/teacherPriority";
import type { BasicCascadePreviewResponse } from "@/types/teacher-request";
import { sortLessonsByDateTime } from "@/utils/lessonSort";
import { substitutionRoomLabel } from "@/utils/lessonRoom";
import {
  lessonDayDateCellClass,
  lessonDayGroup,
  lessonDayRowClass,
} from "@/utils/lessonDayGroup";
import {
  onCascadeStateChanged,
  dispatchCascadeStateChanged,
} from "@/utils/cascadeEvents";
import { useAuthStore } from "@/store";
import { FinanceChoiceRow } from "@/components/teacher/FinanceChoiceRow";
import {
  directorUiEnabled,
  financeUiEnabled,
  showRequesterDeductionBand,
} from "@/utils/financeUi";
import { teacherFinanceApi } from "@/services/api/teacher";
import { extractErrorMessage } from "@/utils/errorHandler";
import { getPrepPagePath } from "@/utils/prepNavigation";
import { documentWorkOpen } from "@/utils/documentDue";
import { directMessageApi } from "@/services/api/directMessages";
import type { DmDirectoryUser } from "@/types/direct-message";

export interface PriorityCardExpiry {
  text: string;
  color: string;
}

export interface PriorityRequestCardProps {
  request: PriorityRequestListItem;
  expiryInfo: PriorityCardExpiry | null;
  isCancelling?: string | null;
  isPrepResolving: string | null;
  onCancel: (id: string, status: string) => void;
  onMarkPrepFulfilled: (id: string) => void;
  onReRequest: (id: string) => void;
  onSendReminder?: (requestId: string, customMessage?: string) => Promise<void>;
  onSendMessage?: (requestId: string) => void;
  onRemoveLesson?: (lessonId: string) => Promise<void>;
  onFinanceChanged?: () => void;
  isHighlighted?: boolean;
  isDimmed?: boolean;
  onDismissHighlight?: () => void;
  suppressInternalFetches?: boolean;
  cascadePreviewFetcher?: (
    requestId: string
  ) => Promise<BasicCascadePreviewResponse>;
  autoRefreshPreview?: boolean;
  onDocumentsChanged?: () => void;
}

type CardTab = "cascade" | "timeline";

const PAYER_LABELS: Record<string, string> = {
  school: "Schule zahlt",
  private: "Privat zahlt",
};

export function PriorityRequestCard({
  request,
  expiryInfo,
  isCancelling,
  isPrepResolving,
  onCancel,
  onMarkPrepFulfilled,
  onReRequest,
  onSendReminder,
  onSendMessage,
  onRemoveLesson,
  onFinanceChanged,
  isHighlighted,
  isDimmed,
  onDismissHighlight,
  suppressInternalFetches,
  cascadePreviewFetcher,
  autoRefreshPreview,
  onDocumentsChanged,
}: PriorityRequestCardProps) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const coverage = request.coverage_summary;
  // storno-v1 (BUG-2 defensive): Eine ausgefuehrte Stornierung (cancelled_at
  // gesetzt) wird IMMER als final behandelt, selbst wenn das Status-Feld
  // driftet — sonst zeigt die Karte "In Bearbeitung", obwohl niemand mehr
  // zustimmen muss.
  const effectiveStatus =
    request.cancelled_at && request.status !== "cancelled"
      ? "cancelled"
      : request.status;
  // Finaler Request-Status dominiert das Kaskaden-Aggregat (Drift-Fix): eine
  // stornierte/abgelaufene/erschoepfte Karte zeigt ihren finalen Status, auch
  // wenn ein residuales cascade_status (z.B. 'partially_accepted') vorliegt.
  const displayStatus = isFinalRequestStatus(effectiveStatus)
    ? effectiveStatus
    : request.combo_search_pending
      ? "distribution_running"
      : (coverage?.cascade_status ??
        getDisplayStatus(request.status, request.composite_status));
  // frost-v2 (E16/B6): Frost-Akzent hängt am Request-Status, nicht am
  // displayStatus (cascade_status könnte den Gate-Status überlagern).
  const directorOn = directorUiEnabled(request.director_enabled);
  const gateFrozen = directorOn && request.status === "director_pending";
  const borderColor = gateFrozen
    ? "border-l-sky-300"
    : getBorderLColor(displayStatus);
  const isPast =
    request.date &&
    new Date(request.date) < new Date(new Date().toDateString());
  const teachersPending = request.coverage_summary?.teachers_pending ?? 0;

  const [removingLessonId, setRemovingLessonId] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Final = the search has ended (no further automatic cascade action). The
  // requesting teacher may still Cancel / "Neue Anfrage" for these (see footer),
  // but the card is visually settled and countdowns/preview-refresh are stopped.
  const isFinal = [
    "cancelled",
    "expired",
    "filled",
    "declined",
    "no_substitute_found",
  ].includes(effectiveStatus);
  // v5: show the cascade-preview button when enabled and not in a terminal phase.
  // Works for cascade AND teacher_matching/smart_batch flows since the preview
  // uses the same TeacherLessonMatcher engine behind both.
  const PREVIEW_STRATEGIES = ["smart_batch", "teacher_matching"];
  const showCascadePreview =
    !!request.cascade_preview_enabled &&
    !!request.request_flow_strategy &&
    PREVIEW_STRATEGIES.includes(request.request_flow_strategy) &&
    !(
      request.phase &&
      ["resolved", "declined", "cancelled"].includes(request.phase)
    );
  const [lessons, setLessons] = useState<RequestLesson[]>([]);
  const [isLoadingLessons, setIsLoadingLessons] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [cascadePreview, setCascadePreview] =
    useState<BasicCascadePreviewResponse | null>(null);
  const genRef = useRef(0);

  const [activeTab, setActiveTab] = useState<CardTab>("cascade");
  const [timelineActivated, setTimelineActivated] = useState(false);

  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderMessage, setReminderMessage] = useState("");
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [showMessageTargetDialog, setShowMessageTargetDialog] = useState(false);
  const [directorCandidates, setDirectorCandidates] = useState<
    DmDirectoryUser[]
  >([]);
  const [directorMessageBusy, setDirectorMessageBusy] = useState(false);
  const [directorMessageError, setDirectorMessageError] = useState<
    string | null
  >(null);

  // prio-v2 (P1/E1): Fallback-Bestätigungszustand
  const [confirmingFallback, setConfirmingFallback] = useState(false);
  const [fallbackError, setFallbackError] = useState<string | null>(null);
  const [deductionOverride, setDeductionOverride] = useState<
    "ipb" | "salary" | null
  >(null);
  const [financeSaving, setFinanceSaving] = useState(false);
  const [financeError, setFinanceError] = useState<string | null>(null);

  useEffect(() => {
    setDeductionOverride(null);
  }, [request.id, request.requester_deduction]);

  const deductionPersonal =
    deductionOverride ?? request.requester_deduction ?? null;

  const handleDeductionSwitch = async (value: string) => {
    if (value !== "ipb" && value !== "salary") return;
    setFinanceSaving(true);
    setFinanceError(null);
    const previous = deductionPersonal;
    setDeductionOverride(value);
    try {
      await teacherFinanceApi.setRequesterDeduction(request.id, value);
      onFinanceChanged?.();
    } catch (err: unknown) {
      setDeductionOverride(previous);
      setFinanceError(
        extractErrorMessage(err, { loadError: "Speichern fehlgeschlagen" })
      );
    } finally {
      setFinanceSaving(false);
    }
  };

  // Injectierbarer Preview-Fetcher (Admin-Kontext: adminApi.getCascadePreview).
  // Wenn gesetzt, wird die Vorschau auch trotz suppressInternalFetches geladen
  // (Admin braucht die Vorschau; Lessons kommen weiterhin aus block_summaries).
  const fetchPreview =
    cascadePreviewFetcher ?? teacherPriorityApi.getCascadePreview;
  const canFetchPreview = cascadePreviewFetcher
    ? true
    : !suppressInternalFetches;

  useEffect(() => {
    if (!canFetchPreview) return;
    const gen = ++genRef.current;
    fetchPreview(request.id)
      .then((data) => {
        if (gen === genRef.current) setCascadePreview(data);
      })
      .catch(() => {});
  }, [
    request.id,
    request.status,
    showCascadePreview,
    canFetchPreview,
    fetchPreview,
  ]);

  // Auto-refresh preview every 30s for active requests.
  // autoRefreshPreview=false (Admin-Setting) schaltet das Auto-Refresh ab.
  const allowAutoRefresh =
    autoRefreshPreview === undefined ? true : autoRefreshPreview;
  useEffect(() => {
    if (isFinal || !showCascadePreview || !canFetchPreview || !allowAutoRefresh)
      return;
    const interval = setInterval(() => {
      const gen = ++genRef.current;
      fetchPreview(request.id)
        .then((data) => {
          if (gen === genRef.current) setCascadePreview(data);
        })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [
    request.id,
    isFinal,
    showCascadePreview,
    canFetchPreview,
    allowAutoRefresh,
    fetchPreview,
  ]);

  useEffect(() => {
    return onCascadeStateChanged((eventId) => {
      if (eventId && eventId !== request.id) return;
      setLessons([]);
      if (!canFetchPreview) {
        setCascadePreview(null);
        return;
      }
      const gen = ++genRef.current;
      fetchPreview(request.id)
        .then((data) => {
          if (gen === genRef.current) setCascadePreview(data);
        })
        .catch(() => {
          if (gen === genRef.current) setCascadePreview(null);
        });
    });
  }, [request.id, canFetchPreview, fetchPreview]);

  const handleTabChange = (tab: CardTab) => {
    setActiveTab(tab);
    if (tab === "timeline") setTimelineActivated(true);
  };

  const handleReminderClick = () => {
    setShowMessageTargetDialog(false);
    setShowReminderDialog(true);
  };

  const handleSendReminder = async () => {
    if (!onSendReminder) return;
    setIsSendingReminder(true);
    try {
      await onSendReminder(request.id, reminderMessage || undefined);
      setShowReminderDialog(false);
      setReminderMessage("");
    } finally {
      setIsSendingReminder(false);
    }
  };

  const closeMessageTargetDialog = () => {
    setShowMessageTargetDialog(false);
    setDirectorCandidates([]);
    setDirectorMessageError(null);
    setDirectorMessageBusy(false);
  };

  const handleMessageClick = () => {
    if (!onSendMessage) return;
    setShowReminderDialog(false);
    if (gateFrozen) {
      setDirectorCandidates([]);
      setDirectorMessageError(null);
      setShowMessageTargetDialog(true);
      return;
    }
    onSendMessage(request.id);
  };

  const handleMessageToSubstitutes = () => {
    closeMessageTargetDialog();
    onSendMessage?.(request.id);
  };

  const openDirectorConversation = async (userId: string) => {
    setDirectorMessageBusy(true);
    setDirectorMessageError(null);
    try {
      const conv = await directMessageApi.createConversation([userId]);
      closeMessageTargetDialog();
      navigate(`/messages?c=${conv.conversation_id}`);
    } catch (e: unknown) {
      setDirectorMessageError(
        (e as { detail?: string })?.detail ||
          "Direktor-Chat konnte nicht geöffnet werden"
      );
    } finally {
      setDirectorMessageBusy(false);
    }
  };

  const handleMessageToDirector = async () => {
    setDirectorMessageBusy(true);
    setDirectorMessageError(null);
    try {
      const dir = await directMessageApi.getDirectory(undefined, "director");
      if (dir.users.length === 0) {
        setDirectorMessageError("Kein Direktor gefunden");
        return;
      }
      const onlyDirector = dir.users[0];
      if (dir.users.length === 1 && onlyDirector) {
        const conv = await directMessageApi.createConversation([
          onlyDirector.user_id,
        ]);
        closeMessageTargetDialog();
        navigate(`/messages?c=${conv.conversation_id}`);
        return;
      }
      setDirectorCandidates(dir.users);
    } catch (e: unknown) {
      setDirectorMessageError(
        (e as { detail?: string })?.detail ||
          "Direktor-Chat konnte nicht geöffnet werden"
      );
    } finally {
      setDirectorMessageBusy(false);
    }
  };

  // debug-fix-v2 (Bug: Top-Tabelle blieb orange): Wenn sich der Request-Status
  // oder die Coverage-Summary ändert (silentRefresh im Parent refresht nur die
  // Request-Liste), werden die einmal gecachten Lektionsdaten invalidiert und
  // beim nächsten Hover neu gefetcht.
  const lessonsFingerprint = useMemo(
    () =>
      JSON.stringify([
        request.status,
        request.coverage_summary?.accepted ?? null,
        request.coverage_summary?.declined ?? null,
        request.coverage_summary?.waiting ?? null,
        request.coverage_summary?.cascade_status ?? null,
      ]),
    [request.status, request.coverage_summary]
  );

  useEffect(() => {
    setLessons([]);
  }, [lessonsFingerprint, request.id]);

  const hasSummaryLessons = (
    request.coverage_summary?.block_summaries ?? []
  ).some((b) => (b.lessons?.length ?? 0) > 0);

  useEffect(() => {
    if (
      request.lesson_count <= 1 ||
      lessons.length > 0 ||
      suppressInternalFetches
    )
      return;
    if (hasSummaryLessons) return;
    if (effectiveStatus !== "cancelled" && !isHovered) return;
    setIsLoadingLessons(true);
    teacherApi
      .getRequestLessons(request.id)
      .then((data) => setLessons(sortLessonsByDateTime(data.lessons)))
      .catch(() => setLessons([]))
      .finally(() => setIsLoadingLessons(false));
  }, [
    request.id,
    request.lesson_count,
    lessons.length,
    suppressInternalFetches,
    hasSummaryLessons,
    effectiveStatus,
    isHovered,
  ]);

  const blockLessons = useMemo(() => {
    if (lessons.length > 0) return [];
    const result: RequestLesson[] = [];
    for (const block of request.coverage_summary?.block_summaries ?? []) {
      const slotName =
        block.time_slot_names?.join(", ") || block.start_time || null;
      for (const l of block.lessons ?? []) {
        result.push({
          id: l.id,
          date: block.date || null,
          time_slot_name: l.time_slot_name || slotName,
          subject_name: l.subject || null,
          klass_name: l.klass || null,
          room_name: l.room || null,
          status: l.assignment_state || block.status || "pending",
          assignment_state: l.assignment_state || block.status || "pending",
          assigned_teacher_id: l.assigned_teacher_id || null,
          assigned_teacher_short_code: l.assigned_teacher_short_code || null,
        });
      }
    }
    return sortLessonsByDateTime(result);
  }, [lessons.length, request.coverage_summary?.block_summaries]);

  // Check if any lesson is HR-relevant (confirmed_at or status === 'booked')
  const allLessons = lessons.length > 0 ? lessons : blockLessons;
  const hasHrRelevantLessons = useMemo(() => {
    return allLessons.some(
      (lesson) => lesson.confirmed_at || lesson.status === "booked"
    );
  }, [allLessons]);

  const effectiveLessons = blockLessons.length > 0 ? blockLessons : lessons;

  // Cancelled lessons are shown as greyed-out rows (not removed) so the
  // "Storniert" tab can display the card with its details.
  const visibleLessons = effectiveLessons;

  const isRemovable = (lesson: RequestLesson): boolean => {
    const nonRemovable = [
      "confirmed",
      "accepted",
      "assigned",
      "filled",
      "booked",
      "cancelled",
    ];
    return !nonRemovable.includes(
      lesson.assignment_state || lesson.status || ""
    );
  };

  const singleOwnLesson: RequestLesson = visibleLessons[0] ?? {
    id: `${request.id}-lesson`,
    date: request.date,
    time_slot_name: request.time_slot_name || null,
    subject_name: request.subject_name || null,
    klass_name: request.klass_name || null,
    room_name: null,
    status: request.status,
    assignment_state: request.status,
  };

  const renderLessonStatus = (lesson: RequestLesson) => {
    const state = lesson.assignment_state;
    const assignedCode = lesson.assigned_teacher_short_code;
    // Owner-Perspektive (debug-fix-v2): 'Anfragen von mir' zeigt vergeben → grün,
    // unabhängig davon, welcher Lehrer übernommen hat.
    const style = getLessonRowStyle(
      state,
      lesson.assigned_teacher_id,
      user?.teacher_id,
      "owner"
    );

    if (state === "cancelled" || lesson.status === "cancelled") {
      return <span className={`text-xs ${style.text}`}>✗ Storniert</span>;
    }

    if (state === "confirmed" || state === "accepted") {
      if (assignedCode) {
        return (
          <span className={`text-xs font-medium ${style.text}`}>
            ✓ {assignedCode}
          </span>
        );
      }
      return (
        <span className={`text-xs font-medium ${style.text}`}>
          ✓ Übernommen
        </span>
      );
    }

    if (state === "declined") {
      return <span className={`text-xs ${style.text}`}>✗ Abgelehnt</span>;
    }

    if (state === "waiting") {
      return <span className={`text-xs ${style.text}`}>◷ Wartepool</span>;
    }

    return <span className={`text-xs ${style.text}`}>○ Offen</span>;
  };

  if (import.meta.env.DEV && coverage) {
    const coverageTotal =
      (coverage.accepted || 0) +
      (coverage.declined || 0) +
      (coverage.waiting || 0);
    if (coverageTotal > request.lesson_count) {
      console.warn(
        `Coverage mismatch for request ${request.id}: lesson_count=${request.lesson_count}, coverage_total=${coverageTotal}`
      );
    }
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("de-DE", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    } catch {
      return "—";
    }
  };

  const opacityClass =
    displayStatus === "cancelled" || displayStatus === "expired"
      ? "opacity-50"
      : displayStatus === "declined"
        ? "opacity-70"
        : displayStatus === "in_progress"
          ? "opacity-90"
          : isFinal || (isPast && displayStatus !== "filled")
            ? "opacity-70"
            : "";

  const DOC_HARD_CLOSED = ["cancelled", "expired", "failed", "declined"];
  const DOC_TERMINAL = ["accepted", "booked", ...DOC_HARD_CLOSED];
  const hasDoc = !!(request.has_document ?? request.documents_received);
  const documentsOutstanding =
    directorOn &&
    documentWorkOpen(
      Boolean(request.document_due),
      request.documents_requested_at,
      hasDoc
    );
  const docFlagsOn =
    directorOn &&
    !!(request.documents_requested_at || hasDoc || request.document_due);
  const gateStatus =
    directorOn &&
    (request.status === "director_pending" ||
      request.status === "director_returned");
  const canUploadDocuments =
    !DOC_HARD_CLOSED.includes(request.status) &&
    (!DOC_TERMINAL.includes(request.status) || documentsOutstanding);
  const canDeleteDocuments = !DOC_TERMINAL.includes(request.status);
  const showDocumentsPanel =
    !DOC_HARD_CLOSED.includes(request.status) && (docFlagsOn || gateStatus);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openDocumentUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      id={`request-card-${request.id}`}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 ${borderColor} p-4 transition-all relative ${
        isHighlighted ? "highlighted-card" : isDimmed ? "dimmed-card" : ""
      } ${opacityClass}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={`Anfrage ${request.subject_name || ""} am ${formatDate(request.date)}`}
      aria-live={isHighlighted ? "polite" : undefined}
    >
      {isHighlighted && onDismissHighlight && (
        <button
          onClick={onDismissHighlight}
          className="absolute top-2 right-2 p-1 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition z-10"
          aria-label="Highlight entfernen"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      {isHighlighted && (
        <span className="sr-only">Anfrage hervorgehoben vom Radar</span>
      )}
      {/* frost-v2 (E16/B7): eisblauer Gradient-Balken links — border-image würde
          mit rounded-xl brechen; absoluter Balken ist radius-sicher. */}
      {gateFrozen && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-sky-300 to-blue-200 pointer-events-none"
          aria-hidden="true"
          data-testid="frost-border"
        />
      )}
      {/* Header — frost-v2 (E16): dezenter Frost-Gradient im Kartenkopf */}
      <div
        className="flex items-center justify-between mb-2"
        style={
          gateFrozen
            ? {
                background:
                  "linear-gradient(135deg, rgba(224,242,254,0.35), transparent 40%)",
              }
            : undefined
        }
      >
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {formatDate(request.date)}
          {request.date_to && <> — {formatDate(request.date_to)}</>}
        </span>
        <div className="flex items-center gap-2">
          {request.created_on_behalf && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800 font-medium">
              durch Admin erstellt
            </span>
          )}
          {request.is_urgent && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-red-500 text-white font-medium">
              EILIG
            </span>
          )}
          {/* frost-v2 (E16/B6): Frost-Badge unabhängig vom cascade-Badge-Zweig —
              sichtbar, solange der Request-Status director_pending ist. */}
          {gateFrozen && (
            <span
              className="px-2 py-0.5 text-xs rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300 font-medium"
              data-testid="frost-badge"
            >
              ❄ {getStatusLabel("director_pending")}
            </span>
          )}
          {request.cancellation_pending && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-orange-500 text-white font-medium animate-pulse">
              Storno offen
            </span>
          )}
          {documentsOutstanding &&
            (canUploadDocuments ? (
              <button
                type="button"
                onClick={openDocumentUpload}
                className="px-1.5 py-0.5 text-xs rounded bg-amber-500 text-white font-medium hover:bg-amber-600"
              >
                Dokumente ausstehend
              </button>
            ) : (
              <span className="px-1.5 py-0.5 text-xs rounded bg-amber-500 text-white font-medium">
                Dokumente ausstehend
              </span>
            ))}
          {request.combo_search_pending && !gateFrozen && (
            <span
              className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor("distribution_running")}`}
            >
              Verteilung läuft · Suche in {formatMatchEta(request.lesson_count)}
            </span>
          )}
          {(!coverage?.cascade_status ||
            isFinalRequestStatus(effectiveStatus)) &&
            !gateFrozen &&
            !request.combo_search_pending && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(displayStatus)}`}
              >
                {getStatusLabel(displayStatus)}
              </span>
            )}
          {!!request.uncovered_dates?.length && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
              {request.uncovered_dates.length === 1
                ? "1 Tag ohne Vertretung"
                : `${request.uncovered_dates.length} Tage ohne Vertretung`}
            </span>
          )}
        </div>
      </div>
      {directorOn &&
        (request.director_decision_type || request.director_payment_type) && (
          <div
            className="flex flex-wrap items-center gap-1.5 mb-2"
            data-testid="director-decision-line"
          >
            {request.director_decision_type &&
              request.director_decision_type !== "info_requested" && (
                <span
                  className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                    request.director_decision_type === "approved"
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                      : request.director_decision_type === "rejected"
                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                        : "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
                  }`}
                >
                  Direktor:{" "}
                  {DECISION_TYPE_LABELS[request.director_decision_type] ||
                    request.director_decision_type}
                </span>
              )}
            {request.director_payment_type && (
              <span
                className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                  request.director_payment_type === "school"
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                }`}
              >
                {PAYER_LABELS[request.director_payment_type] ||
                  request.director_payment_type}
              </span>
            )}
          </div>
        )}
      {showRequesterDeductionBand(request, deductionPersonal) &&
        !suppressInternalFetches && (
          <div className="mb-2">
            <FinanceChoiceRow
              groupName={`requester-deduction-${request.id}`}
              title="Abzug (privat):"
              options={[
                { value: "ipb", label: "IPB" },
                { value: "salary", label: "Lohnabzug" },
              ]}
              personalValue={deductionPersonal}
              schoolDefault={request.requester_school_default ?? "ipb"}
              canEdit={request.can_change_requester_finance === true}
              lockReason={request.finance_lock_reason ?? null}
              bindingAt={request.finance_binding_at}
              pendingHint={
                !deductionPersonal && request.show_requester_finance_hint
                  ? "Abzug wählbar, sobald der Zahler privat ist."
                  : null
              }
              saving={financeSaving}
              error={financeError}
              onSwitch={handleDeductionSwitch}
            />
          </div>
        )}

      {/* Konzept A (IPB-Vereinheitlichung): Prep-Band an derselben Stelle wie
          die Finanz-Wahl — Badge + Aktionen statt Footer-Buttons. Gleiche
          Optik/Position wie auf «Anfragen an mich». */}
      <div className="mb-2 flex items-center gap-2 flex-wrap text-xs bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded px-2.5 py-1.5">
        <span className="font-medium text-purple-800 dark:text-purple-200">
          Prep:
        </span>
        {request.has_open_prep_request && (
          <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200 rounded-full">
            angefragt
          </span>
        )}
        <button
          onClick={() => navigate(getPrepPagePath(request.id, "outgoing"))}
          className="px-2 py-1 text-xs text-purple-600 dark:text-purple-400 border border-purple-300 dark:border-purple-600 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 font-medium"
        >
          Prep öffnen
        </button>
        {request.has_open_prep_request && (
          <button
            onClick={() => onMarkPrepFulfilled(request.id)}
            disabled={isPrepResolving === request.id}
            className="px-2 py-1 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50 disabled:opacity-50 dark:text-green-400 dark:border-green-600 dark:hover:bg-green-900/30"
          >
            {isPrepResolving === request.id ? "..." : "Prep erledigt"}
          </button>
        )}
      </div>

      {/* E8: Dokumente-Nachreich (Badge «Dokumente ausstehend» + Upload) */}
      {showDocumentsPanel && (
        <RequestDocumentsSection
          requestId={request.id}
          fileInputRef={fileInputRef}
          canUpload={canUploadDocuments}
          canDelete={canDeleteDocuments}
          onDocumentsChanged={onDocumentsChanged}
        />
      )}

      {/* Subject / Class / Room or Lesson Table */}
      {request.lesson_count === 1 ? (
        <>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {request.subject_name || "—"} • {request.klass_name || "—"}
          </div>
          <div className="text-xs text-gray-500 mb-3">
            {request.lesson_count} Lektion
            {request.lesson_count !== 1 ? "en" : ""} •{" "}
            {request.request_source === "admin_direct"
              ? "Admin-Zuweisung"
              : request.request_flow === "automatic"
                ? "Automatisch"
                : "Priorität"}
          </div>
          {renderLessonStatus(singleOwnLesson)}
          {onRemoveLesson && !isFinal && isRemovable(singleOwnLesson) && (
            <button
              type="button"
              onClick={() => {
                setRemovingLessonId(singleOwnLesson.id);
                setShowRemoveConfirm(true);
                setRemoveError(null);
              }}
              className="mb-3 text-xs text-red-600"
              aria-label={`Lektion ${singleOwnLesson.subject_name || ""} entfernen`}
            >
              Entfernen
            </button>
          )}
        </>
      ) : (
        <>
          <div className="text-xs text-gray-500 mb-2">
            {request.lesson_count} Lektionen •{" "}
            {request.request_source === "admin_direct"
              ? "Admin-Zuweisung"
              : request.request_flow === "automatic"
                ? "Automatisch"
                : "Priorität"}
          </div>
          {isLoadingLessons ? (
            <div className="animate-pulse space-y-2 mb-3">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          ) : visibleLessons.length > 0 ? (
            <div className="overflow-x-auto mb-3">
              <table
                className="w-full text-xs"
                aria-label={`Details für ${request.lesson_count} Lektionen`}
              >
                <thead className="text-[10px] text-gray-500 dark:text-gray-400 uppercase border-b">
                  <tr>
                    <th scope="col" className="pb-1 text-left font-medium">
                      Datum
                    </th>
                    <th scope="col" className="pb-1 text-left font-medium">
                      Zeit
                    </th>
                    <th scope="col" className="pb-1 text-left font-medium">
                      Fach
                    </th>
                    <th scope="col" className="pb-1 text-left font-medium">
                      Klasse
                    </th>
                    <th scope="col" className="pb-1 text-left font-medium">
                      Raum
                    </th>
                    <th scope="col" className="pb-1 text-right font-medium">
                      Status
                    </th>
                    {onRemoveLesson && !isFinal && (
                      <th scope="col" className="pb-1 w-8"></th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {visibleLessons.map((lesson, index) => {
                    const style = getLessonRowStyle(
                      lesson.assignment_state,
                      lesson.assigned_teacher_id,
                      user?.teacher_id,
                      "owner"
                    );
                    const { firstOfDay, dayIndex, isDayBreak } = lessonDayGroup(
                      visibleLessons,
                      index
                    );
                    const dateLabel = lesson.date
                      ? new Date(lesson.date).toLocaleDateString("de-DE", {
                          weekday: "short",
                          day: "numeric",
                          month: "numeric",
                        })
                      : "-";
                    const room = substitutionRoomLabel(
                      lesson,
                      "owner",
                      user?.teacher_id
                    );
                    return (
                      <tr
                        key={lesson.id}
                        className={`${style.row} ${lessonDayRowClass(dayIndex, isDayBreak)}`}
                      >
                        <td
                          className={`py-1.5 ${style.text} ${lessonDayDateCellClass(dayIndex)}`}
                        >
                          {firstOfDay ? (
                            dateLabel
                          ) : (
                            <span className="sr-only">{dateLabel}</span>
                          )}
                        </td>
                        <td className={`py-1.5 ${style.text}`}>
                          {lesson.time_slot_name || "-"}
                        </td>
                        <td className={`py-1.5 font-medium ${style.text}`}>
                          {lesson.subject_name || "-"}
                        </td>
                        <td className={`py-1.5 ${style.text}`}>
                          {lesson.klass_name || "-"}
                        </td>
                        <td className={`py-1.5 ${style.text}`}>{room || ""}</td>
                        <td className="py-1.5 text-right">
                          {renderLessonStatus(lesson)}
                        </td>
                        {onRemoveLesson && !isFinal && (
                          <td className="py-1.5">
                            {isRemovable(lesson) ? (
                              <button
                                onClick={() => {
                                  setRemovingLessonId(lesson.id);
                                  setShowRemoveConfirm(true);
                                  setRemoveError(null);
                                }}
                                disabled={removingLessonId === lesson.id}
                                className="p-0.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                                title="Lektion aus Anfrage entfernen"
                                aria-label={`Lektion ${lesson.subject_name || ""} entfernen`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <span
                                className="text-[10px] text-gray-400"
                                title="Lektion bereits vergeben"
                              >
                                —
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-3">
              Details nicht verfügbar
            </div>
          )}
        </>
      )}

      {/* Coverage Bar — bei finalen Status (Storniert/Abgelaufen/Erschöpft)
          ausgeblendet, da eine "0/4 — wartet"-Anzeige dann irreführend ist. */}
      {coverage && !isFinal && (
        <CoverageBar
          accepted={coverage.accepted}
          declined={coverage.declined}
          waiting={coverage.waiting}
          lesson_count={request.lesson_count}
        />
      )}

      {/* Tab-Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mt-3">
        <nav
          className="flex -mb-px"
          role="tablist"
          aria-label="Anfrage-Details"
        >
          <button
            role="tab"
            aria-selected={activeTab === "cascade"}
            aria-controls="panel-cascade"
            id="tab-cascade"
            onClick={() => handleTabChange("cascade")}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                e.preventDefault();
                handleTabChange("timeline");
              }
            }}
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "cascade"
                ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            Kaskade
          </button>
          <button
            role="tab"
            aria-selected={activeTab === "timeline"}
            aria-controls="panel-timeline"
            id="tab-timeline"
            onClick={() => handleTabChange("timeline")}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                handleTabChange("cascade");
              }
            }}
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "timeline"
                ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            Verlauf
          </button>
        </nav>
      </div>

      {/* prio-v2 (P1/E1): Fallback-Bestätigung — Prioritätsliste erschöpft, */}
      {/* automatische Suche wartet auf die Bestätigung des anfragenden Lehrers */}
      {request.fallback_confirmation_pending && !isFinal && (
        <div className="mx-4 mb-2 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-center justify-between gap-3 dark:bg-amber-900/30 dark:border-amber-600">
          <div className="text-xs text-amber-900 dark:text-amber-100">
            <span className="font-semibold block mb-0.5">
              Prioritätsliste erschöpft
            </span>
            Alle priorisierten Lehrpersonen haben geantwortet. Automatische
            Vertretungssuche starten?
          </div>
          <button
            onClick={async () => {
              setConfirmingFallback(true);
              try {
                await teacherPriorityApi.confirmFallback(request.id);
                dispatchCascadeStateChanged(request.id);
              } catch {
                setFallbackError(
                  "Bestätigung fehlgeschlagen — bitte erneut versuchen."
                );
              } finally {
                setConfirmingFallback(false);
              }
            }}
            disabled={confirmingFallback}
            className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50 whitespace-nowrap"
          >
            {confirmingFallback ? "..." : "Automatische Suche starten"}
          </button>
        </div>
      )}
      {fallbackError && (
        <p className="mx-4 mb-2 text-xs text-red-600 dark:text-red-400">
          {fallbackError}
        </p>
      )}

      {/* Kaskade-Tab */}
      {activeTab === "cascade" && (
        <div
          role="tabpanel"
          id="panel-cascade"
          aria-labelledby="tab-cascade"
          className="pt-3"
        >
          {request.current_answer_deadline && !isFinal && (
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Nächste Antwortfrist:
              </span>
              <ResponseCountdown
                deadline={request.current_answer_deadline}
                variant="badge"
                size="sm"
                label="Nächste Antwortfrist"
              />
            </div>
          )}
          {coverage &&
          (coverage.teachers.length > 0 ||
            (cascadePreview?.current_teachers?.length ?? 0) > 0) ? (
            <PriorityCascadeIndicator
              teachers={coverage.teachers}
              cascadeStatus={coverage.cascade_status}
              requestStatus={effectiveStatus}
              requestMode={request.request_mode}
              requestFlow={request.request_flow}
              preview={cascadePreview}
              maxBadges={request.max_visible_cascade_badges ?? 0}
              isResolved={
                isFinal ||
                ["resolved", "accepted", "filled"].includes(request.phase || "")
              }
            />
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
              Keine Kaskaden-Daten verfügbar
            </p>
          )}
        </div>
      )}

      {/* Verlauf-Tab: visibility toggle, lazy mount */}
      {timelineActivated && (
        <div
          role="tabpanel"
          id="panel-timeline"
          aria-labelledby="tab-timeline"
          className={`pt-3 ${activeTab !== "timeline" ? "hidden" : ""}`}
        >
          <PriorityCardTimelineTab request={request} />
        </div>
      )}

      {/* Footer: Expiry + Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2 flex-wrap">
          {expiryInfo && !isFinal && (
            <span className={`text-xs ${expiryInfo.color}`}>
              {expiryInfo.text}
            </span>
          )}
          {!expiryInfo && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Erstellt:{" "}
              {new Date(request.created_at).toLocaleDateString("de-DE")}
            </span>
          )}
          {request.current_answer_deadline && !isFinal && (
            <ResponseCountdown
              deadline={request.current_answer_deadline}
              variant="badge"
              size="sm"
              label="Nächste Antwortfrist"
            />
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap">
          {documentsOutstanding && canUploadDocuments && (
            <button
              type="button"
              onClick={openDocumentUpload}
              className="px-2 py-1 text-xs text-amber-700 dark:text-amber-300 border border-amber-400 dark:border-amber-600 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30 font-medium"
            >
              Dokumente hochladen
            </button>
          )}
          {/* Cancel: the requesting teacher may abort any non-cancelled request.
              expired/failed are now allowed (with warning in frontend).
              declined/no_substitute_found mean the search ended (nobody found or all
              declined) — the owner may still formally cancel/re-request regardless of
              the cascade aggregate (which can read 'in_progress' for a frozen cascade). */}
          {request.status !== "cancelled" &&
            request.request_source !== "admin_direct" && (
              <div className="relative group">
                <button
                  onClick={() => onCancel(request.id, request.status)}
                  disabled={isCancelling === request.id}
                  className="px-2 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/30"
                >
                  {isCancelling === request.id ? "..." : "Stornieren"}
                </button>
                {hasHrRelevantLessons &&
                  financeUiEnabled(request.hr_module_enabled) && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded shadow-lg text-xs text-yellow-900 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 dark:bg-yellow-900/90 dark:border-yellow-600 dark:text-yellow-100">
                      <div className="font-semibold mb-1">
                        ⚠️ HR-Warnhinweis
                      </div>
                      <div>
                        Diese Anfrage enthält Lektionen die an das HR für die
                        Vergütung gehen würden.
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                        <div className="border-4 border-transparent border-t-yellow-300 dark:border-t-yellow-600"></div>
                      </div>
                    </div>
                  )}
              </div>
            )}

          {/* Declined: re-request */}
          {(request.status === "declined" ||
            request.status === "no_substitute_found") && (
            <button
              onClick={() => onReRequest(request.id)}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              Neue Anfrage
            </button>
          )}

          {/* Reminder (pending + ausstehende Lehrer) */}
          {(request.status === "pending" ||
            request.status === "partially_filled") &&
            teachersPending > 0 &&
            onSendReminder && (
              <button
                onClick={handleReminderClick}
                className="px-2 py-1 text-xs text-orange-600 dark:text-orange-400 border border-orange-300 dark:border-orange-600 rounded hover:bg-orange-50 dark:hover:bg-orange-900/30"
              >
                Erinnerung
              </button>
            )}

          {/* Message */}
          {onSendMessage && (
            <button
              onClick={handleMessageClick}
              className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
            >
              Nachricht
            </button>
          )}
          <CascadeLegendTrigger />
        </div>
      </div>

      {showMessageTargetDialog && (
        <div className="mt-3 p-3 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
          {directorCandidates.length === 0 ? (
            <>
              <p className="text-sm text-sky-900 dark:text-sky-100 font-medium mb-1">
                Für wen ist die Nachricht?
              </p>
              <p className="text-xs text-sky-800 dark:text-sky-300 mb-2">
                Die Anfrage ist eingefroren. Nachricht an die Stellvertreter
                oder an den Direktor (wegen Freeze)?
              </p>
            </>
          ) : (
            <p className="text-sm text-sky-900 dark:text-sky-100 font-medium mb-2">
              Welcher Direktor?
            </p>
          )}
          {directorMessageError && (
            <p className="text-xs text-red-600 dark:text-red-400 mb-2">
              {directorMessageError}
            </p>
          )}
          <div className="flex gap-2 flex-wrap">
            {directorCandidates.length === 0 ? (
              <>
                <button
                  type="button"
                  onClick={handleMessageToSubstitutes}
                  disabled={directorMessageBusy}
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  Stellvertreter
                </button>
                <button
                  type="button"
                  onClick={() => void handleMessageToDirector()}
                  disabled={directorMessageBusy}
                  className="px-3 py-1 text-sm bg-sky-600 text-white rounded hover:bg-sky-700 disabled:opacity-50"
                >
                  {directorMessageBusy ? "…" : "Direktor"}
                </button>
              </>
            ) : (
              directorCandidates.map((d) => (
                <button
                  key={d.user_id}
                  type="button"
                  onClick={() => void openDirectorConversation(d.user_id)}
                  disabled={directorMessageBusy}
                  className="px-3 py-1 text-sm bg-sky-600 text-white rounded hover:bg-sky-700 disabled:opacity-50"
                >
                  {d.name}
                </button>
              ))
            )}
            <button
              type="button"
              onClick={closeMessageTargetDialog}
              disabled={directorMessageBusy}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Reminder Dialog */}
      {showReminderDialog && (
        <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
          <textarea
            value={reminderMessage}
            onChange={(e) => setReminderMessage(e.target.value)}
            placeholder="Optionale Nachricht (sonst Standardtext)..."
            className="w-full p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
            rows={2}
            maxLength={500}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSendReminder}
              disabled={isSendingReminder}
              className="px-3 py-1 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
            >
              {isSendingReminder ? "Senden..." : "Senden"}
            </button>
            <button
              onClick={() => {
                setShowReminderDialog(false);
                setReminderMessage("");
              }}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Remove Lesson Confirmation Dialog */}
      {showRemoveConfirm && removingLessonId && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300 mb-2">
            Diese Lektion wirklich aus der Anfrage entfernen?
          </p>
          {removeError && (
            <p className="text-xs text-red-600 dark:text-red-400 mb-2">
              {removeError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (!onRemoveLesson || !removingLessonId) return;
                setRemoveError(null);
                try {
                  await onRemoveLesson(removingLessonId);
                  setShowRemoveConfirm(false);
                  setRemovingLessonId(null);
                  setLessons([]);
                } catch (e: unknown) {
                  setRemoveError(
                    (e as { detail?: string })?.detail ||
                      "Fehler beim Entfernen"
                  );
                }
              }}
              className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
            >
              Entfernen
            </button>
            <button
              onClick={() => {
                setShowRemoveConfirm(false);
                setRemovingLessonId(null);
                setRemoveError(null);
              }}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** E8/EC3: Dokumente-Nachreich in «Meine Anfragen» — Liste, Upload, Löschen,
 *  Download. Nur für den anfragenden Lehrer (Backend erzwingt E9). */
function RequestDocumentsSection({
  requestId,
  fileInputRef,
  canUpload,
  canDelete,
  onDocumentsChanged,
}: {
  requestId: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  canUpload: boolean;
  canDelete: boolean;
  onDocumentsChanged?: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const {
    data: docs = [],
    refetch,
    isLoading,
  } = useQuery<SubstitutionRequestDocument[]>({
    queryKey: ["request-documents", requestId],
    queryFn: () => teacherPriorityApi.getRequestDocuments(requestId),
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setDocError(null);
    try {
      await teacherPriorityApi.uploadRequestDocument(requestId, file);
      await refetch();
      onDocumentsChanged?.();
    } catch (err: unknown) {
      setDocError(
        (err as { detail?: string })?.detail || "Upload fehlgeschlagen"
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await teacherPriorityApi.deleteRequestDocument(requestId, docId);
      await refetch();
      onDocumentsChanged?.();
    } catch (err: unknown) {
      setDocError(
        (err as { detail?: string })?.detail || "Löschen fehlgeschlagen"
      );
    }
  };

  const handleDownload = async (docId: string, filename: string) => {
    try {
      const blob = await teacherPriorityApi.downloadRequestDocument(
        requestId,
        docId
      );
      const url = URL.createObjectURL(blob as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setDocError(
        (err as { detail?: string })?.detail || "Download fehlgeschlagen"
      );
    }
  };

  return (
    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Dokumente (Attest)
        </span>
        {docs.length > 0 && (
          <span className="text-[11px] text-gray-400">
            {docs.length} Datei(en)
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-xs text-gray-400">Laden…</p>
      ) : docs.length > 0 ? (
        <ul className="space-y-1 mb-2">
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between text-xs"
            >
              <span className="truncate max-w-[220px]">{d.filename}</span>
              <span className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleDownload(d.id, d.filename)}
                  className="text-blue-600 hover:underline"
                >
                  ↓
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(d.id)}
                    className="text-red-500 hover:underline"
                  >
                    ✕
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-400 mb-1.5">
          Noch keine Dokumente eingereicht.
        </p>
      )}

      {canUpload && (
        <label className="inline-flex items-center gap-1.5 text-xs text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          />
          {uploading ? "Wird hochgeladen…" : "Datei hochladen"}
        </label>
      )}
      {canUpload && (
        <p className="text-[11px] text-gray-400 mt-1">
          PDF, JPG, PNG, Word/Excel · max. 10 MB gesamt
        </p>
      )}

      {docError && <p className="text-xs text-red-600 mt-1">{docError}</p>}
    </div>
  );
}
